// src/components/ui/Button.tsx
//
// The Button primitive - the most-used UI element in the app.
//
// Built on the Linear-inspired dark design vocabulary:
//   - Multi-color accents (indigo primary, rose for danger)
//   - Semi-transparent borders
//   - Subtle hover states
//   - Loading spinner with disabled state
//   - Keyboard accessible (Enter + Space)
//
// Variants:
//   - primary: filled with accent-indigo (CTAs)
//   - secondary: filled with bg-elevated (secondary actions)
//   - ghost: transparent (tertiary actions)
//   - danger: filled with accent-rose (destructive)
//
// Sizes: sm | md | lg | icon

import { forwardRef, type ButtonHTMLAttributes, type AnchorHTMLAttributes } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * Visual + semantic variant of the button.
 */
export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";

/**
 * Size of the button. `icon` is for square icon-only buttons.
 */
export type ButtonSize = "sm" | "md" | "lg" | "icon";

/**
 * Props shared by both <button> and <a> rendered forms.
 */
interface ButtonBaseProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  disabled?: boolean;
}

/**
 * Props when the button is rendered as a real <button>.
 */
type ButtonAsButtonProps = ButtonBaseProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof ButtonBaseProps> & {
    href?: never;
  };

/**
 * Props when the button is rendered as an <a> (for external links).
 */
type ButtonAsAnchorProps = ButtonBaseProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof ButtonBaseProps> & {
    href: string;
  };

export type ButtonProps = ButtonAsButtonProps | ButtonAsAnchorProps;

/**
 * Style recipes for each variant. Centralized here for easy maintenance.
 */
const variantClasses: Record<ButtonVariant, string> = {
  primary:
    "bg-accent-indigo text-white border border-white/10 hover:bg-accent-indigo/90 active:bg-accent-indigo/80",
  secondary:
    "bg-surface-elevated text-fg-primary border border-border hover:bg-surface-hover active:bg-surface-surface",
  ghost:
    "bg-transparent text-fg-secondary border border-transparent hover:bg-surface-elevated hover:text-fg-primary",
  danger:
    "bg-accent-rose text-white border border-white/10 hover:bg-accent-rose/90",
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: "h-8 px-3 text-xs font-510 rounded",
  md: "h-9 px-4 text-sm font-510 rounded",
  lg: "h-11 px-6 text-base font-510 rounded-md",
  icon: "h-9 w-9 rounded",
};

/**
 * The Button component.
 *
 * Polymorphic: renders as a `<button>` by default, or as an `<a>` if
 * `href` is provided. This avoids a separate `<ButtonLink>` component
 * and makes the API consistent for both forms of action.
 *
 * @example
 *   <Button onClick={save}>Save</Button>
 *   <Button variant="danger" isLoading={deleting}>Delete</Button>
 *   <Button href="https://example.com" target="_blank">External</Button>
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "primary",
    size = "md",
    isLoading = false,
    className,
    children,
    disabled,
    ...rest
  },
  ref,
) {
  const classes = cn(
    // Layout
    "inline-flex items-center justify-center gap-2",
    "transition-colors duration-150",
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-indigo focus-visible:ring-offset-2 focus-visible:ring-offset-bg-canvas",
    "disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none",
    // Variants
    variantClasses[variant],
    // Sizes
    sizeClasses[size],
    // User override
    className,
  );

  // Render as anchor if href is provided
  if ("href" in rest && rest.href !== undefined) {
    const { href, type, ...anchorRest } = rest as ButtonAsAnchorProps;
    return (
      <a
        ref={ref as React.Ref<HTMLAnchorElement>}
        href={href}
        className={cn(classes, isLoading && "pointer-events-none opacity-50")}
        aria-disabled={isLoading || disabled}
        data-variant={variant}
        data-size={size}
        {...anchorRest}
      >
        {isLoading && <Loader2 data-testid="button-spinner" className="h-4 w-4 animate-spin" />}
        {children}
      </a>
    );
  }

  // Render as button
  const buttonRest = rest as ButtonAsButtonProps;
  const { disabled: _disabled, ...buttonProps } = buttonRest as ButtonAsButtonProps & { disabled?: boolean };
  return (
    <button
      ref={ref}
      type={buttonRest.type ?? "button"}
      disabled={disabled || isLoading}
      aria-busy={isLoading}
      className={classes}
      data-variant={variant}
      data-size={size}
      {...buttonProps}
    >
      {isLoading && <Loader2 data-testid="button-spinner" className="h-4 w-4 animate-spin" />}
      {children}
    </button>
  );
});